import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, ButtonGroup } from 'react-bootstrap';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { useResto } from '../contexts/RestoContext';
import config from '../config/config';
import { useAuth } from '../contexts/AuthContext';
import { setFlashMessage, useWebsite } from '../contexts/WebsiteContext';

const RestoParameters = () => {
    const { t } = useTranslation();
    const { restoData, setRestoData } = useResto();
    const { token } = useAuth();
    const { setIsLoading } = useWebsite();

    const [showModal, setShowModal] = useState(false);

    const defaultData = {
        alert: '',
        label: '',
        phone: '',
        availableLanguages: ['fr'],
        socialMediaTripAdvisorUrl: '',
        socialMediaInstagramUrl: '',
        socialMediaYoutubeUrl: '',
        socialMediaFacebookUrl: '',
        mainColor: '128,128,128',
        color2: '255,255,255',
        color3: '240,240,240',
        hasHeaderMenu: false,
        hasNavigationMenu: false,
        hasFooterLogo: false,
        hasSquareSocialMediaIcons: false,
        website: restoData?.website
    };

    const [editedResto, setEditedResto] = useState(defaultData);

    useEffect(() => {
        if (restoData) {
            setEditedResto({
                ...defaultData,
                ...restoData
            });
        }
    }, [restoData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditedResto(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleBooleanChange = (name, value) => {
        setEditedResto(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        try {
            const response = await axios.put(
                `${config.apiUrl}resto/${restoData.website}`,
                editedResto,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setRestoData(response.data);
            setFlashMessage(t('SUCCESS_UPDATED_MESSAGE'), 'success');
            setShowModal(false);
        } catch (error) {
            setFlashMessage(t('ERROR_MESSAGE'), 'error');
        }
        setIsLoading(false);
    };

    const toggleLanguage = (lang) => {
        if (lang === 'fr') return;

        const updated = [...editedResto.availableLanguages];
        const index = updated.indexOf(lang);

        if (index !== -1) {
            updated.splice(index, 1);
        } else {
            updated.push(lang);
        }

        setEditedResto(prev => ({
            ...prev,
            availableLanguages: updated
        }));
    };

    return (
        <>
            <h2>Le site web</h2>

            <div className="kubuni-frame">
                <p><strong>Libellé :</strong> {restoData?.label}</p>
                <p><strong>Téléphone :</strong> {restoData?.phone || <i>Non renseigné</i>}</p>

                <Button variant="primary" onClick={() => setShowModal(true)}>
                    Mettre à jour
                </Button>
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Mettre à jour les paramètres</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form>

                        <Form.Group className="mb-3">
                            <Form.Label>Message d’alerte</Form.Label>
                            <Form.Control
                                type="text"
                                name="alert"
                                value={editedResto.alert}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Libellé</Form.Label>
                            <Form.Control
                                type="text"
                                name="label"
                                value={editedResto.label}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Langues</Form.Label>
                            <ButtonGroup>
                                {['fr', 'en', 'es', 'de'].map(lang => (
                                    <Button
                                        key={lang}
                                        variant={editedResto.availableLanguages?.includes(lang) ? 'primary' : 'outline-primary'}
                                        onClick={() => toggleLanguage(lang)}
                                    >
                                        {lang.toUpperCase()}
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Couleur principale</Form.Label>
                            <Form.Control
                                type="color"
                                value="#888888"
                                onChange={(e) => handleChange({
                                    target: { name: 'mainColor', value: e.target.value }
                                })}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="switch"
                                label="Afficher menu header"
                                checked={!!editedResto.hasHeaderMenu}
                                onChange={(e) =>
                                    handleBooleanChange('hasHeaderMenu', e.target.checked)
                                }
                            />
                        </Form.Group>

                    </Form>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        {t('CANCEL')}
                    </Button>
                    <Button variant="primary" onClick={handleSubmit}>
                        {t('CONFIRM')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default RestoParameters;